/**
 * api.ts — typed fetch client for the Worker API. Every function here
 * parses the response with the matching Zod schema from
 * @demandhub-cms/shared before returning it, so a response that doesn't
 * match the shared contract fails loudly (in dev) instead of silently
 * flowing bad data into the UI. `credentials: "include"` is set on every
 * call so the Better Auth session cookie is sent cross-origin to the
 * Worker (the Worker's CORS config explicitly allows this origin with
 * `credentials: true` — see apps/worker/src/index.ts).
 */
import {
  aiProposalSchema,
  usageSummarySchema,
  webCheckStatusSchema,
  webCheckSchema,
  socialDraftResultSchema,
  aiStatusResponseSchema,
  approveDraftResponseSchema,
  calendarResponseSchema,
  calendarEventSchema,
  holidayCountriesResponseSchema,
  importResultSchema,
  coverageResponseSchema,
  type CoverageGap,
  type CalendarEvent,
  type CalendarEventInput,
  type ImportResult,
  editLockResponseSchema,
  notificationSettingsSchema,
  notificationsResponseSchema,
  testEmailResultSchema,
  type NotificationSettings,
  type NotificationsResponse,
  type UpdateNotificationSettingsInput,
  handoffStateResponseSchema,
  listMemoryRulesResponseSchema,
  listProposalsResponseSchema,
  memoryRuleSchema,
  memorySummarySchema,
  plagiarismResponseSchema,
  apiErrorSchema,
  authorSchema,
  capabilitySchema,
  categorySchema,
  contentSchema,
  contentVersionSchema,
  gitStatusSchema,
  importSummarySchema,
  listAuditLogResponseSchema,
  listAuthorsResponseSchema,
  listCategoriesResponseSchema,
  listContentResponseSchema,
  listUsersResponseSchema,
  promoteToProductionResponseSchema,
  redirectSchema,
  roleSchema,
  rolePermissionSchema,
  sessionUserSchema,
  siteSchema,
  assistResponseSchema,
  bulkResultSchema,
  idResponseSchema,
  siteVariableSchema,
  tagSchema,
  updateSiteVariableResponseSchema,
  uploadMediaResponseSchema,
  inviteUserResponseSchema,
  inviteLinkResultSchema,
  type InviteUserResponse,
  type InviteLinkResult,
  type AiProposal,
  type UsageSummary,
  type WebCheckStatus,
  type SocialDraftBody,
  type SocialDraftResult,
  type SocialIdeaDraftBody,
  type SocialIdeaDraftResult,
  socialIdeaDraftResultSchema,
  type SetPriceBody,
  type AiStatusResponse,
  type ApproveDraftBody,
  type AssistBody,
  type AssistResponse,
  type BulkBody,
  type BulkResult,
  type CalendarResponse,
  type QuickCreateBody,
  type DecideIdeaBody,
  type GenerateDraftBody,
  type GenerateIdeasBody,
  type ListProposalsResponse,
  type EditLockResponse,
  type HandoffStateResponse,
  type MemoryRule,
  type Author,
  type AuditLogEntry,
  type Category,
  type Content,
  type ContentVersion,
  type CreateAuthorInput,
  type CreateCategoryInput,
  type CreateContentInput,
  type CreateRedirectInput,
  type CreateSiteInput,
  type CreateSiteVariableInput,
  type CreateTagInput,
  type GitStatus,
  type InviteUserInput,
  type ListAuditLogQuery,
  type ListContentQuery,
  type ListContentResponse,
  type ListUsersQuery,
  type ListUsersResponse,
  type ImportSummary,
  type ListAuthorsResponse,
  type ListCategoriesResponse,
  type PromoteToProductionResponse,
  type PublishContentInput,
  type Redirect,
  type RejectContentInput,
  generateImagesRequestSchema,
  generateImagesResponseSchema,
  generatedImageSchema,
  imageSettingsSchema,
  type ImageSettings,
  contentCommentSchema,
  type ContentComment,
  type CreateCommentInput,
  type RolePermission,
  type UpdateRolePermissionInput,
  type ScheduleContentInput,
  type SessionUser,
  type Site,
  type SiteVariable,
  type Tag,
  type UpdateAuthorInput,
  type UpdateCategoryInput,
  type UpdateContentInput,
  type UpdateSiteInput,
  type UpdateSiteVariableInput,
  type UpdateSiteVariableResponse,
  type UpdateUserRoleInput,
  type UploadMediaResponse,
  listAppSecretsResponseSchema,
  checkAppSecretResultSchema,
  layoutDetectionSchema,
  type ListAppSecretsResponse,
  type CheckAppSecretResult,
  evidenceListResponseSchema,
  jobsResponseSchema,
  brandProfileResponseSchema,
  socialChannelSchema,
  socialPostSchema,
  socialPostsResponseSchema,
  importSocialCsvResponseSchema,
  socialStatusResponseSchema,
  type CreateSocialPostsBody,
  type SocialChannel,
  type SocialPost,
  type SocialStatus,
  type SocialChannelService,
  keywordRefreshResultSchema,
  seoStatusSchema,
  type KeywordRefreshResult,
  type SeoLocationKey,
  type SeoStatus,
  feedbackSuggestionsResponseSchema,
  type DraftRejectTag,
  knowledgeDocSchema,
  knowledgeListResponseSchema,
  type BrandProfile,
  type BrandProfileResponse,
  type KnowledgeDoc,
  type KnowledgeDocInput,
  type KnowledgeListResponse,
  type EvidenceInput,
  type EvidenceListResponse,
  type EvidenceQuery,
  type JobRun, smStatusResponseSchema, smTestResultSchema, smSyncResultSchema, smSendResultSchema, smAnalyticsResponseSchema, adConceptsResponseSchema, adResearchRunsResponseSchema, adScheduleStatusSchema, type AdSchedule, agentsViewSchema, brandKitViewSchema, type BrandKit, type AdImageKit, adLineageSchema, byTagResponseSchema, type AdOrigin, adLearnedRulesResponseSchema, type LearnedStatus, insightsSchema, alertAnswerResultSchema, type AlertAnswer, googleConnectionSchema, connectionsResponseSchema, type UpdateSocialChannelBody as UpdateChannelBody, socialStageCountsSchema, socialAgentsSchema, socialIdeasSchema, socialReviewSchema, socialInsightsSchema, googleConnectionCreatedSchema, adNameApprovalSchema, adNameApprovalListSchema, adCostReportSchema } from "@demandhub-cms/shared";
import type { SmRange, AdChannel, AdFeedbackTag, AdPayload, AdProductId, AdStatus, AdVerdict } from "@demandhub-cms/shared";
import {
  adDraftSchema,
  adDraftsResponseSchema,
  adEditorExportSchema,
  adEditorExportListSchema,
  adFeedbackRulesResponseSchema,
  adGenerateResultSchema,
  adSceneResultSchema,
  adResultSchema,
  adResultsResponseSchema,
  adsBatchesResponseSchema,
  adsImportResponseSchema,
  apifyFetchResultSchema,
  keywordLookupResultSchema,
  advertisersResultSchema,
  flowSchema,
  socialFlowSchema,
  domainResultSchema,
  apifyStatusSchema,
  adsSummarySchema,
  competitorAdSchema,
  competitorAdsResponseSchema,
  watchlistItemSchema,
  watchlistResponseSchema,
  type AdResultInput,
  type CompetitorAdInput,
  type WatchlistInput,
} from "@demandhub-cms/shared";
import {
  googleItemsResponseSchema, generateSocialIdeasResultSchema, generateSocialPostsResultSchema, opsRunsResponseSchema, promptListSchema, savedSocialIdeaListSchema, socialLearnedRulesResponseSchema, socialThisWeekSchema, testPromptResultSchema,
  type EditablePromptKey, type GenerateSocialIdeasBody, type GenerateSocialPostsBody,
} from "@demandhub-cms/shared";
import { z } from "zod";

const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787";

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
    this.name = "ApiError";
  }
}

/** The one place that turns a failed call into words for a person: the server's own message, else the fallback. */
export function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export async function request<T>(
  // `any, any` for the schema's Input/Def type params: several shared
  // schemas (e.g. contentSchema's `tags` field) use `.default(...)`,
  // which makes their Zod *input* type looser than their *output* type
  // T — we only care about the validated output here.
  path: string,
  schema: z.ZodType<T, any, any>,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: "include",
    headers:
      options.body && !(options.body instanceof FormData)
        ? { "Content-Type": "application/json", ...options.headers }
        : options.headers,
  });

  const json = await res.json().catch(() => null);

  if (!res.ok) {
    const parsedError = apiErrorSchema.safeParse(json);
    throw new ApiError(
      parsedError.success ? parsedError.data.error : `Request failed with status ${res.status}`,
      res.status,
      parsedError.success ? parsedError.data.code : undefined
    );
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    // A response that doesn't match the shared contract is a bug in the
    // worker or a drifted schema — surface it loudly instead of passing
    // malformed data on to the UI.
    console.error("API response failed schema validation", path, parsed.error);
    const first = parsed.error.issues[0];
    throw new ApiError(
      `The server's answer for ${path.split("?")[0]} did not match what the app expects${first ? ` (field “${first.path.join(".") || "root"}”: ${first.message})` : ""}.`,
      res.status,
      "BAD_RESPONSE"
    );
  }
  return parsed.data;
}

export function qs(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/** Plain-language messages for Better Auth's error codes; anything else falls back to the server's own sentence. */
const AUTH_MESSAGES: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: "That email and password do not match.",
  INVALID_PASSWORD: "That password is not right.",
  INVALID_TOKEN: "This link has expired or was already used.",
  INVALID_TWO_FACTOR_COOKIE: "Your sign-in took too long. Enter your password again.",
  INVALID_CODE: "That code is not right. Check the latest code in your app.",
  INVALID_BACKUP_CODE: "That backup code is not right, or was already used.",
  TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE: "Too many wrong codes. Sign in again.",
  ACCOUNT_TEMPORARILY_LOCKED: "Too many wrong codes. This account is locked for 15 minutes.",
  CREDENTIAL_ACCOUNT_NOT_FOUND: "This account signs in with Google, so it has no password to change.",
  USER_NOT_FOUND: "That account no longer exists.",
};

/** Reads a failed Better Auth response into one short sentence. */
async function authFailure(res: Response, fallback: string): Promise<ApiError> {
  const json = (await res.json().catch(() => null)) as { message?: string; code?: string; error?: string } | null;
  if (res.status === 429) {
    const wait = Number(res.headers.get("X-Retry-After") ?? res.headers.get("Retry-After"));
    const when = wait > 90 ? `${Math.ceil(wait / 60)} minutes` : wait > 0 ? `${wait} seconds` : "a few minutes";
    return new ApiError(`Too many tries. Wait ${when} and try again.`, 429, "RATE_LIMITED");
  }
  const code = json?.code;
  return new ApiError((code && AUTH_MESSAGES[code]) || json?.message || json?.error || fallback, res.status, code);
}

async function authPost<T = unknown>(path: string, body: unknown, fallback: string): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/auth${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await authFailure(res, fallback);
  return (await res.json().catch(() => null)) as T;
}

export interface AuthSession {
  id: string;
  token: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  updatedAt: string;
  current: boolean;
}

export const authApi = {
  /** Better Auth's own sign-in endpoint (mounted at /auth/*). `needsCode` is true when the account uses two-step sign-in. */
  async login(email: string, password: string): Promise<{ needsCode: boolean }> {
    const json = await authPost<{ twoFactorRedirect?: boolean } | null>("/sign-in/email", { email, password }, "Sign in failed.");
    return { needsCode: Boolean(json?.twoFactorRedirect) };
  },
  /** Second step: the 6-digit code from the authenticator app. */
  async verifyCode(code: string): Promise<void> {
    await authPost("/two-factor/verify-totp", { code: code.replace(/\s/g, "") }, "That code is not right.");
  },
  /** Second step, no phone: one of the ten backup codes (each works once). */
  async verifyBackupCode(code: string): Promise<void> {
    await authPost("/two-factor/verify-backup-code", { code: code.trim() }, "That backup code is not right.");
  },
  /** "Forgot password": always succeeds from the outside, so it cannot be used to find out who has an account. */
  async requestReset(email: string): Promise<void> {
    await authPost("/request-password-reset", { email, redirectTo: `${window.location.origin}/reset-password` }, "Could not send the link.");
  },
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await authPost("/reset-password", { token, newPassword }, "Could not save the password.");
  },
  async changePassword(currentPassword: string, newPassword: string, signOutOthers: boolean): Promise<void> {
    await authPost("/change-password", { currentPassword, newPassword, revokeOtherSessions: signOutOthers }, "Could not change the password.");
  },
  async sessions(): Promise<AuthSession[]> {
    const [list, current] = await Promise.all([
      fetch(`${API_BASE_URL}/auth/list-sessions`, { credentials: "include" }),
      fetch(`${API_BASE_URL}/auth/get-session`, { credentials: "include" }),
    ]);
    if (!list.ok) throw await authFailure(list, "Could not load your devices.");
    const rows = (await list.json().catch(() => [])) as Array<Record<string, unknown>>;
    const currentToken = ((await current.json().catch(() => null)) as { session?: { token?: string } } | null)?.session?.token;
    return rows
      .map((r) => ({
        id: String(r.id),
        token: String(r.token),
        ipAddress: typeof r.ipAddress === "string" ? r.ipAddress : null,
        userAgent: typeof r.userAgent === "string" ? r.userAgent : null,
        createdAt: String(r.createdAt),
        updatedAt: String(r.updatedAt ?? r.createdAt),
        current: r.token === currentToken,
      }))
      .sort((a, b) => Number(b.current) - Number(a.current) || b.updatedAt.localeCompare(a.updatedAt));
  },
  async revokeSession(token: string): Promise<void> {
    await authPost("/revoke-session", { token }, "Could not sign that device out.");
  },
  async revokeOtherSessions(): Promise<void> {
    await authPost("/revoke-other-sessions", {}, "Could not sign the other devices out.");
  },
  /** Starts two-step setup: returns the authenticator link (for the QR code) and the ten backup codes. Not active until a code is confirmed. */
  async startMfa(password: string): Promise<{ totpURI: string; backupCodes: string[] }> {
    return authPost("/two-factor/enable", { password }, "Could not start setup.");
  },
  async confirmMfa(code: string): Promise<void> {
    await authPost("/two-factor/verify-totp", { code: code.replace(/\s/g, "") }, "That code is not right.");
  },
  async disableMfa(password: string): Promise<void> {
    await authPost("/two-factor/disable", { password }, "Could not turn it off.");
  },
  async newBackupCodes(password: string): Promise<string[]> {
    const json = await authPost<{ backupCodes: string[] }>("/two-factor/generate-backup-codes", { password }, "Could not make new codes.");
    return json.backupCodes;
  },
  /** Whether this person must turn on two-step sign-in before using the app (managers who sign in with a password). */
  /** How far this computer's clock is from the server's, in seconds (positive = this computer is ahead). Null when it cannot be told. */
  async clockSkewSeconds(): Promise<number | null> {
    try {
      const t0 = Date.now();
      const r = await request("/auth/server-time", z.object({ now: z.string() }));
      const t1 = Date.now();
      const server = Date.parse(r.now);
      return Number.isFinite(server) ? Math.round(((t0 + t1) / 2 - server) / 1000) : null;
    } catch {
      return null;
    }
  },
  async logout(): Promise<void> {
    await fetch(`${API_BASE_URL}/auth/sign-out`, { method: "POST", credentials: "include" });
  },
  /** Starts "Sign in with Google". Better Auth's social sign-in endpoint
   * returns `{url}` JSON (it does NOT redirect itself) — the caller must
   * navigate the browser there. Only works for an email a manager already
   * invited; an uninvited Google account is rejected and sent back to
   * `errorCallbackURL` with `?error=...`. */
  async signInWithGoogle(): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/auth/sign-in/social`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        provider: "google",
        callbackURL: `${window.location.origin}/`,
        errorCallbackURL: `${window.location.origin}/login`,
      }),
    });
    if (!res.ok) {
      const json = await res.json().catch(() => null);
      throw new ApiError(json?.message ?? "Couldn't start Google sign-in.", res.status);
    }
    const json = await res.json();
    window.location.href = json.url;
  },
  /** Returns the current session's user, or null if not authenticated.
   * Better Auth's session payload is normalized to just {user} with our
   * additionalFields at the top level via this client-side reshaping,
   * since the raw Better Auth session envelope carries more than the
   * admin UI needs. */
  async getSession(): Promise<SessionUser | null> {
    const res = await fetch(`${API_BASE_URL}/auth/get-session`, { credentials: "include" });
    if (!res.ok) return null;
    const json = await res.json().catch(() => null);
    if (!json?.user) return null;
    const parsed = sessionUserSchema.safeParse(json.user);
    return parsed.success ? parsed.data : null;
  },
};

// ---------------------------------------------------------------------------
// Sites
// ---------------------------------------------------------------------------

export const sitesApi = {
  list: () => request("/sites", z.object({ items: z.array(siteSchema) })).then((r) => r.items),
  get: (id: string) => request<Site>(`/sites/${id}`, siteSchema),
  create: (body: CreateSiteInput) =>
    request<Site>("/sites", siteSchema, { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: UpdateSiteInput) =>
    request<Site>(`/sites/${id}`, siteSchema, { method: "PATCH", body: JSON.stringify(body) }),
  delete: (id: string) =>
    request<{ ok: true }>(`/sites/${id}`, z.object({ ok: z.literal(true) }), { method: "DELETE" }),
};

export const secretsApi = {
  list: () => request<ListAppSecretsResponse>("/secrets", listAppSecretsResponseSchema),
  save: (name: string, value: string) =>
    request<{ ok: true }>(`/secrets/${name}`, z.object({ ok: z.literal(true) }), { method: "PUT", body: JSON.stringify({ value }) }),
  remove: (name: string) => request<{ ok: true }>(`/secrets/${name}`, z.object({ ok: z.literal(true) }), { method: "DELETE" }),
  check: (name: string) => request<CheckAppSecretResult>(`/secrets/${name}/check`, checkAppSecretResultSchema, { method: "POST", body: JSON.stringify({}) }),
};

export const notificationsApi = {
  get: (siteId: string) => request<NotificationsResponse>(`/notifications?siteId=${encodeURIComponent(siteId)}`, notificationsResponseSchema),
  update: (siteId: string, body: UpdateNotificationSettingsInput) =>
    request<NotificationSettings>(`/notifications/${siteId}`, notificationSettingsSchema, { method: "PUT", body: JSON.stringify(body) }),
  test: () => request<{ ok: boolean; message: string }>("/notifications/test", testEmailResultSchema, { method: "POST", body: JSON.stringify({}) }),
};

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

export const contentApi = {
  // `sort` defaults to "publishDate_desc" (bug fix: "list should have
  // 'published date newest first' by default" — matching the server's own
  // default in listContentQuerySchema) so every caller that doesn't care
  // about sort order (ReviewQueue.tsx, ContentEditor.tsx's related-content
  // search, etc.) keeps compiling and gets the same new default; only
  // ContentList.tsx passes an explicit value from its own sort control.
  list: (query: Omit<ListContentQuery, "sort"> & { sort?: ListContentQuery["sort"] }) =>
    request<ListContentResponse>(
      `/content${qs({ sort: "publishDate_desc", ...query } as Record<string, string | number | undefined>)}`,
      listContentResponseSchema
    ),
  get: (id: string) => request<Content>(`/content/${id}`, contentSchema),
  // The editing hold. Opening an item takes it; the open page renews it; leaving releases it.
  lockState: (id: string) => request<EditLockResponse>(`/content/${id}/lock`, editLockResponseSchema),
  lockAcquire: (id: string, force = false) =>
    request<EditLockResponse>(`/content/${id}/lock`, editLockResponseSchema, { method: "POST", body: JSON.stringify({ force }) }),
  lockHeartbeat: (id: string) => request<EditLockResponse>(`/content/${id}/lock/heartbeat`, editLockResponseSchema, { method: "POST" }),
  // keepalive lets the request finish while the tab is closing.
  lockRelease: (id: string) => request<EditLockResponse>(`/content/${id}/lock/release`, editLockResponseSchema, { method: "POST", keepalive: true }),
  versions: (id: string) =>
    request(`/content/${id}/versions`, z.object({ items: z.array(contentVersionSchema) })).then(
      (r) => r.items as ContentVersion[]
    ),
  create: (body: CreateContentInput) =>
    request<Content>("/content", contentSchema, { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: UpdateContentInput) =>
    request<Content>(`/content/${id}`, contentSchema, { method: "PATCH", body: JSON.stringify(body) }),
  submit: (id: string) =>
    request<Content>(`/content/${id}/submit`, contentSchema, { method: "POST" }),
  remove: (id: string) => request(`/content/${id}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
  comments: (id: string) => request<ContentComment[]>(`/content/${id}/comments`, z.array(contentCommentSchema)),
  addComment: (id: string, body: CreateCommentInput) =>
    request<ContentComment>(`/content/${id}/comments`, contentCommentSchema, { method: "POST", body: JSON.stringify(body) }),
  resolveComment: (id: string, commentId: string, resolved: boolean) =>
    request<ContentComment>(`/content/${id}/comments/${commentId}`, contentCommentSchema, { method: "PATCH", body: JSON.stringify({ resolved }) }),
  removeComment: (id: string, commentId: string) => request(`/content/${id}/comments/${commentId}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
  reject: (id: string, body: RejectContentInput) =>
    request<Content>(`/content/${id}/reject`, contentSchema, { method: "POST", body: JSON.stringify(body) }),
  publish: (id: string, body: PublishContentInput) => request<Content>(`/content/${id}/publish`, contentSchema, { method: "POST", body: JSON.stringify(body) }),
  schedule: (id: string, body: ScheduleContentInput) =>
    request<Content>(`/content/${id}/schedule`, contentSchema, { method: "POST", body: JSON.stringify(body) }),
  unschedule: (id: string) =>
    request<Content>(`/content/${id}/unschedule`, contentSchema, { method: "POST" }),
  archive: (id: string) => request<Content>(`/content/${id}/archive`, contentSchema, { method: "POST" }),
  rollback: (id: string, versionId: string) =>
    request<Content>(`/content/${id}/rollback/${versionId}`, contentSchema, { method: "POST" }),
};

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

export interface UploadMediaOptions {
  /** Nests the upload under the real per-type image folder (e.g.
   * src/assets/images/demandhub/articles/<slug>/...) — see
   * apps/worker/src/routes/media.ts. Omit for a flat, generic upload. */
  contentType?: "article" | "case_study" | "ebook" | "author";
  slug?: string;
  /** Overrides the original filename (still sanitized server-side) — used
   * by the RichTextEditor's inline-image button so a body image gets a
   * predictable, real-convention name instead of the browser's raw upload
   * filename. */
  filename?: string;
}

export const voiceApi = {
  /** Whether the server has the speech service attached. Lets the screen explain it before anyone records. */
  status: () => request("/voice/status", z.object({ ready: z.boolean() })),
  /** One recording (WAV) in, its text out. */
  transcribe: (audio: Blob) => {
    const form = new FormData();
    form.append("audio", audio, "recording.wav");
    return request("/voice/transcribe", z.object({ text: z.string() }), { method: "POST", body: form });
  },
};

export const imageSettingsResponseSchema = imageSettingsSchema.extend({ monthSpentUsd: z.number() });
export type ImageSettingsResponse = z.infer<typeof imageSettingsResponseSchema>;

async function binaryFail(res: Response): Promise<never> {
  const json = await res.json().catch(() => null);
  const parsed = apiErrorSchema.safeParse(json);
  throw new ApiError(parsed.success ? parsed.data.error : `Request failed with status ${res.status}`, res.status, parsed.success ? parsed.data.code : undefined);
}

export const imagesApi = {
  generate: (body: z.input<typeof generateImagesRequestSchema>) =>
    request("/images/generate", generateImagesResponseSchema, { method: "POST", body: JSON.stringify(body) }),
  list: (siteId: string) => request(`/images${qs({ siteId })}`, z.array(generatedImageSchema)),
  remove: (id: string) => request(`/images/${id}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
  settings: (siteId: string) => request(`/images/settings${qs({ siteId })}`, imageSettingsResponseSchema),
  saveSettings: (siteId: string, body: ImageSettings) =>
    request("/images/settings", imageSettingsResponseSchema, { method: "PUT", body: JSON.stringify({ siteId, ...body }) }),
  /** The original picture exactly as it was made. */
  file: async (id: string): Promise<Blob> => {
    const res = await fetch(`${API_BASE_URL}/images/${id}/file`, { credentials: "include" });
    if (!res.ok) return binaryFail(res);
    return res.blob();
  },
  /** Keeps a picture the person chose from their own device, so it can be used like a made one. */
  upload: async (siteId: string, purpose: string, file: File): Promise<z.infer<typeof generatedImageSchema>> => {
    const res = await fetch(`${API_BASE_URL}/images/upload${qs({ siteId, purpose, name: file.name })}`, { method: "POST", credentials: "include", headers: { "Content-Type": file.type }, body: file });
    if (!res.ok) return binaryFail(res);
    return generatedImageSchema.parse(await res.json());
  },
  /** Stores the finished picture at a public address (for social posts). No body shares the untouched original. */
  publicLink: async (id: string, finished?: Blob): Promise<string> => {
    const res = await fetch(`${API_BASE_URL}/images/${id}/export`, {
      method: "POST",
      credentials: "include",
      ...(finished ? { headers: { "Content-Type": finished.type }, body: finished } : {}),
    });
    if (!res.ok) return binaryFail(res);
    const json = z.object({ url: z.string().url() }).parse(await res.json());
    return json.url;
  },
};

export const mediaApi = {
  upload: (file: File, siteId: string, branch: "staging" | "production", options: UploadMediaOptions = {}) => {
    const form = new FormData();
    form.append("file", file);
    form.append("siteId", siteId);
    form.append("branch", branch);
    if (options.contentType) form.append("contentType", options.contentType);
    if (options.slug) form.append("slug", options.slug);
    if (options.filename) form.append("filename", options.filename);
    return request<UploadMediaResponse>("/media/upload", uploadMediaResponseSchema, {
      method: "POST",
      body: form,
    });
  },
  /** Fetches a committed image as a base64 data: URI for a real inline
   * thumbnail. Returns null on 404 (nothing committed at that path yet)
   * instead of throwing — that's an expected, common state (a brand-new
   * draft's image fields are usually still empty), not an error. */
  preview: async (siteId: string, path: string, branch: "staging" | "production" = "staging"): Promise<string | null> => {
    try {
      const res = await request<{ dataUrl: string }>(
        `/media/preview${qs({ siteId, path, branch })}`,
        z.object({ dataUrl: z.string() })
      );
      return res.dataUrl;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
};

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

export const tagsApi = {
  list: (siteId: string) =>
    request(`/tags${qs({ siteId })}`, z.object({ items: z.array(tagSchema) })).then((r) => r.items as Tag[]),
  create: (body: CreateTagInput) =>
    request<Tag>("/tags", tagSchema, { method: "POST", body: JSON.stringify(body) }),
  delete: (id: string) => request(`/tags/${id}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export const categoriesApi = {
  // Unpaginated — used by pickers (e.g. ContentEditor.tsx's category
  // combobox) that need the FULL list to search/select from. Omitting
  // page/pageSize from the request (see listCategoriesQuerySchema's doc
  // comment) makes the worker return every row, unchanged from before the
  // pagination bug fix below.
  list: (siteId: string) =>
    request(`/categories${qs({ siteId })}`, z.object({ items: z.array(categorySchema) })).then(
      (r) => r.items as Category[]
    ),
  // Bug fix ("there is no pagination"): the Categories & Authors settings
  // screen's own paginated view — same page/pageSize/total shape as
  // contentApi.list, for a consistent pagination UI across the app.
  listPaged: (siteId: string, opts: { page: number; pageSize: number; search?: string }) =>
    request<ListCategoriesResponse>(
      `/categories${qs({ siteId, page: opts.page, pageSize: opts.pageSize, search: opts.search })}`,
      listCategoriesResponseSchema
    ),
  create: (body: CreateCategoryInput) =>
    request<Category>("/categories", categorySchema, { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: UpdateCategoryInput) =>
    request<Category>(`/categories/${id}`, categorySchema, { method: "PATCH", body: JSON.stringify(body) }),
  delete: (id: string) => request(`/categories/${id}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Site variables
// ---------------------------------------------------------------------------

export const variablesApi = {
  list: (siteId: string) =>
    request(`/variables${qs({ siteId })}`, z.object({ items: z.array(siteVariableSchema) })).then(
      (r) => r.items as SiteVariable[]
    ),
  create: (body: CreateSiteVariableInput) =>
    request<SiteVariable>("/variables", siteVariableSchema, { method: "POST", body: JSON.stringify(body) }),
  // Returns { variable, republished, republishFailures } — see
  // updateSiteVariableResponseSchema — so the caller can show exactly how
  // many live articles just got re-committed as a result of this change.
  update: (id: string, body: UpdateSiteVariableInput) =>
    request<UpdateSiteVariableResponse>(`/variables/${id}`, updateSiteVariableResponseSchema, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  delete: (id: string) => request(`/variables/${id}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Authors
// ---------------------------------------------------------------------------

export const authorsApi = {
  // Unpaginated — used by pickers (e.g. ContentEditor.tsx's author
  // combobox) that need the FULL list to search/select from. See
  // categoriesApi.list's comment above; same design.
  list: (siteId: string) =>
    request(`/authors${qs({ siteId })}`, z.object({ items: z.array(authorSchema) })).then(
      (r) => r.items as Author[]
    ),
  // Bug fix ("there is no pagination"): the Categories & Authors settings
  // screen's own paginated view.
  listPaged: (siteId: string, opts: { page: number; pageSize: number; search?: string }) =>
    request<ListAuthorsResponse>(
      `/authors${qs({ siteId, page: opts.page, pageSize: opts.pageSize, search: opts.search })}`,
      listAuthorsResponseSchema
    ),
  create: (body: CreateAuthorInput) =>
    request<Author>("/authors", authorSchema, { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: UpdateAuthorInput) =>
    request<Author>(`/authors/${id}`, authorSchema, { method: "PATCH", body: JSON.stringify(body) }),
  delete: (id: string) => request(`/authors/${id}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Redirects
// ---------------------------------------------------------------------------

export const redirectsApi = {
  list: (siteId: string) =>
    request(`/redirects${qs({ siteId })}`, z.object({ items: z.array(redirectSchema) })).then(
      (r) => r.items as Redirect[]
    ),
  create: (body: CreateRedirectInput) =>
    request<Redirect>("/redirects", redirectSchema, { method: "POST", body: JSON.stringify(body) }),
  delete: (id: string) => request(`/redirects/${id}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export const usersApi = {
  list: (query: ListUsersQuery) =>
    request<ListUsersResponse>(`/users${qs(query as Record<string, string | number | undefined>)}`, listUsersResponseSchema),
  invite: (body: InviteUserInput) =>
    request<InviteUserResponse>("/users/invite", inviteUserResponseSchema, { method: "POST", body: JSON.stringify(body) }),
  /** A fresh one-time set-password link (emailed, or handed back when email is not set up). */
  sendLink: (id: string) => request<InviteLinkResult>(`/users/${id}/send-link`, inviteLinkResultSchema, { method: "POST" }),
  /** Recovery: turns off someone's two-step sign-in (lost phone and backup codes). */
  resetMfa: (id: string) => request(`/users/${id}/reset-mfa`, z.object({ ok: z.boolean() }), { method: "POST" }),
  /** Replaces the list of sites a person may open (managers always open all). */
  setSites: (id: string, siteIds: string[]) =>
    request(`/users/${id}/sites`, z.object({ id: z.string(), siteIds: z.array(z.string()) }), { method: "PUT", body: JSON.stringify({ siteIds }) }),
  changeRole: (id: string, body: UpdateUserRoleInput) =>
    request(`/users/${id}/role`, z.object({ ok: z.boolean() }), { method: "PATCH", body: JSON.stringify(body) }),
  deactivate: (id: string) =>
    request(`/users/${id}/deactivate`, z.object({ ok: z.boolean() }), { method: "POST" }),
  activate: (id: string) =>
    request(`/users/${id}/activate`, z.object({ ok: z.boolean() }), { method: "POST" }),
  delete: (id: string) => request(`/users/${id}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export const auditApi = {
  list: (query: ListAuditLogQuery) =>
    request(
      `/audit${qs(query as Record<string, string | number | undefined>)}`,
      listAuditLogResponseSchema
    ),
};
export type { AuditLogEntry };

// ---------------------------------------------------------------------------
// Git / deploy
// ---------------------------------------------------------------------------

export const gitApi = {
  status: (siteId: string) => request<GitStatus>(`/git/status${qs({ siteId })}`, gitStatusSchema),
  promote: (siteId: string) =>
    request<PromoteToProductionResponse>(`/git/promote${qs({ siteId })}`, promoteToProductionResponseSchema, {
      method: "POST",
    }),
  // Real functional gap fix: imports the site's already-live content,
  // categories and authors from GitHub into D1 — see
  // apps/worker/src/lib/importSync.ts's file header for the full story
  // (this CMS's tables previously only ever held what was created
  // through the CMS itself, so the real site's ~200 existing articles
  // never showed up here).
  import: (siteId: string, branch: "staging" | "production" = "production") =>
    request<ImportSummary>(`/git/import${qs({ siteId, branch })}`, importSummarySchema, { method: "POST" }),
  // "Connect GitHub" (manager-only): token, repositories, branches and the plain-language checklist.
  connect: {
    token: (owner?: string) => request("/git/connect/token" + qs({ owner }), githubTokenStatusSchema),
    saveToken: (token: string) => request("/git/connect/token", githubSaveTokenSchema, { method: "PUT", body: JSON.stringify({ token }) }),
    removeToken: () => request("/git/connect/token", z.object({ ok: z.boolean() }), { method: "DELETE" }),
    repos: () => request("/git/connect/repos", githubReposSchema),
    branches: (repo: string) => request("/git/connect/branches" + qs({ repo }), githubBranchesSchema),
    createBranch: (repo: string, name: string, from: string) =>
      request("/git/connect/branch", z.object({ ok: z.boolean() }), { method: "POST", body: JSON.stringify({ repo, name, from }) }),
    check: (repo: string, productionBranch: string, stagingBranch: string) =>
      request("/git/connect/check", githubCheckSchema, { method: "POST", body: JSON.stringify({ repo, productionBranch, stagingBranch }) }),
    /** Reads a standard Astro site's own settings and says where its posts go (or what to ask). */
    detect: (repo: string, branch: string, answers: Record<string, string> = {}) =>
      request("/git/connect/detect", layoutDetectionSchema, { method: "POST", body: JSON.stringify({ repo, branch, answers }) }),
  },
};

const githubViewerSchema = z.object({
  login: z.string(),
  name: z.string().nullable(),
  avatarUrl: z.string(),
  expiresAt: z.string().nullable(),
  kind: z.enum(["fine_grained", "classic", "unknown"]),
});
export const githubTokenStatusSchema = z.object({
  configured: z.boolean(),
  source: z.enum(["cms", "worker"]).nullable(),
  viewer: githubViewerSchema.nullable(),
  problem: z.string().nullable(),
  encryptionReady: z.boolean(),
  newTokenUrl: z.string(),
});
const githubSaveTokenSchema = z.object({ ok: z.boolean(), viewer: githubViewerSchema });
export const githubReposSchema = z.object({
  repos: z.array(z.object({ fullName: z.string(), private: z.boolean(), defaultBranch: z.string(), pushedAt: z.string().nullable(), canPush: z.boolean() })),
  truncated: z.boolean(),
});
export const githubBranchesSchema = z.object({ branches: z.array(z.string()), defaultBranch: z.string(), canPush: z.boolean(), truncated: z.boolean() });
export const githubCheckSchema = z.object({
  ready: z.boolean(),
  items: z.array(z.object({ id: z.string(), ok: z.boolean(), label: z.string(), detail: z.string() })),
  layout: z.object({ layout: z.enum(["demandhub", "astro_standard", "astro_other", "not_astro"]), summary: z.string(), supported: z.boolean() }).passthrough().nullable(),
  detection: layoutDetectionSchema.nullable().optional(),
});
export type GithubTokenStatus = z.infer<typeof githubTokenStatusSchema>;
export type GithubCheck = z.infer<typeof githubCheckSchema>;

// ---------------------------------------------------------------------------
// Role permissions (item 7 — granular RBAC). Manager-only on the server;
// see pages/RolePermissions.tsx.
// ---------------------------------------------------------------------------

const listRolePermissionsResponseSchema = z.object({
  items: z.array(rolePermissionSchema),
  roles: z.array(z.string()),
  capabilities: z.array(z.string()),
});

export const rolePermissionsApi = {
  list: () => request("/role-permissions", listRolePermissionsResponseSchema),
  update: (body: UpdateRolePermissionInput) =>
    request<RolePermission>("/role-permissions", rolePermissionSchema, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  // Any authenticated user's own effective capabilities — powers the
  // client-side RequireCapability guard (ProtectedRoute.tsx) so a
  // publisher who's been granted a capability can actually reach the
  // screen for it, not just pass the server-side check if they guessed
  // the URL. See routes/rolePermissions.ts's GET /mine doc comment.
  mine: () =>
    request("/role-permissions/mine", z.object({ role: roleSchema, capabilities: z.array(capabilitySchema) })),
};

// ---------------------------------------------------------------------------
// Calendar (v2)
// ---------------------------------------------------------------------------

export const calendarEventsApi = {
  list: (siteId: string) => request<{ items: CalendarEvent[] }>(`/calendar-events${qs({ siteId })}`, z.object({ items: z.array(calendarEventSchema) })),
  create: (siteId: string, body: CalendarEventInput) =>
    request<CalendarEvent>(`/calendar-events${qs({ siteId })}`, calendarEventSchema, { method: "POST", body: JSON.stringify(body) }),
  remove: (id: string) => request<{ ok: true }>(`/calendar-events/${id}`, z.object({ ok: z.literal(true) }), { method: "DELETE" }),
  importIcs: (siteId: string, ics: string) =>
    request<ImportResult>("/calendar-events/import-ics", importResultSchema, { method: "POST", body: JSON.stringify({ siteId, ics }) }),
  countries: () => request("/calendar-events/holiday-countries", holidayCountriesResponseSchema),
  importHolidays: (siteId: string, country: string, year: number) =>
    request<ImportResult>("/calendar-events/import-holidays", importResultSchema, { method: "POST", body: JSON.stringify({ siteId, country, year }) }),
};

export const calendarCoverageApi = {
  coverage: (siteId: string) => request("/calendar/coverage" + qs({ siteId }), coverageResponseSchema),
};
export type { CoverageGap };

export const calendarApi = {
  get: (siteId: string, from: string, to: string) =>
    request<CalendarResponse>(`/calendar${qs({ siteId, from, to })}`, calendarResponseSchema),
  reschedule: (id: string, scheduledFor: string) =>
    request(`/calendar/${id}/reschedule`, z.object({ id: z.string(), scheduledFor: z.string() }), {
      method: "POST",
      body: JSON.stringify({ scheduledFor }),
    }),
  quickCreate: (body: QuickCreateBody) =>
    request("/calendar/quick-create", idResponseSchema, { method: "POST", body: JSON.stringify(body) }),
  plan: (id: string, plannedFor: string | null) =>
    request(`/calendar/${id}/plan`, idResponseSchema, { method: "POST", body: JSON.stringify({ plannedFor }) }),
  duplicate: (id: string, plannedFor?: string | null) =>
    request(`/calendar/${id}/duplicate`, idResponseSchema, { method: "POST", body: JSON.stringify({ plannedFor }) }),
  bulk: (body: BulkBody) =>
    request<BulkResult>("/calendar/bulk", bulkResultSchema, { method: "POST", body: JSON.stringify(body) }),
};

// ---------------------------------------------------------------------------
// AI layer (v2). The AI only proposes; approvals below are the human gates.
// ---------------------------------------------------------------------------

const memorySummaryResponseSchema = memorySummarySchema;

export const aiApi = {
  status: (siteId: string) => request<AiStatusResponse>(`/ai/status${qs({ siteId })}`, aiStatusResponseSchema),
  generateIdeas: (body: GenerateIdeasBody) =>
    request<AiProposal>("/ai/ideas", aiProposalSchema, { method: "POST", body: JSON.stringify(body) }),
  generateDraft: (body: GenerateDraftBody) =>
    request<AiProposal>("/ai/drafts", aiProposalSchema, { method: "POST", body: JSON.stringify(body) }),
  proposals: (query: { siteId: string; kind?: "topic_ideas" | "article_draft"; status?: AiProposal["status"]; q?: string; page?: number; pageSize?: number }) =>
    request<ListProposalsResponse>(`/ai/proposals${qs(query)}`, listProposalsResponseSchema),
  assist: (body: AssistBody) =>
    request<AssistResponse>("/ai/assist", assistResponseSchema, { method: "POST", body: JSON.stringify(body) }),
  webCheckStatus: () => request<WebCheckStatus>("/ai/webcheck/status", webCheckStatusSchema),
  webCheck: (id: string) => request<AiProposal>(`/ai/proposals/${id}/webcheck`, aiProposalSchema, { method: "POST" }),
  contentWebCheck: (contentId: string) => request(`/ai/content/${contentId}/webcheck`, z.object({ check: webCheckSchema.nullable() })),
  runContentWebCheck: (contentId: string) => request(`/ai/content/${contentId}/webcheck`, z.object({ check: webCheckSchema.nullable() }), { method: "POST" }),
  socialIdeaDrafts: (body: SocialIdeaDraftBody) => request<SocialIdeaDraftResult>("/ai/social-idea-drafts", socialIdeaDraftResultSchema, { method: "POST", body: JSON.stringify(body) }),
  socialDrafts: (body: SocialDraftBody) => request<SocialDraftResult>("/ai/social-drafts", socialDraftResultSchema, { method: "POST", body: JSON.stringify(body) }),
  contentAiCost: (contentId: string) => request(`/ai/content/${contentId}/cost`, z.object({ costUsd: z.number().nullable(), calls: z.number().int(), unpricedCalls: z.number().int() })),
  usage: (siteId: string) => request<UsageSummary>(`/ai/usage${qs({ siteId })}`, usageSummarySchema),
  setPrice: (body: SetPriceBody) => request("/ai/prices", z.object({ ok: z.boolean() }), { method: "PUT", body: JSON.stringify(body) }),
  deleteProposal: (id: string) => request(`/ai/proposals/${id}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
  proposal: (id: string) => request<AiProposal>(`/ai/proposals/${id}`, aiProposalSchema),
  decideIdea: (id: string, body: DecideIdeaBody) =>
    request<AiProposal>(`/ai/proposals/${id}/ideas/decide`, aiProposalSchema, { method: "POST", body: JSON.stringify(body) }),
  approveDraft: (id: string, body: ApproveDraftBody) =>
    request(`/ai/proposals/${id}/approve`, approveDraftResponseSchema, { method: "POST", body: JSON.stringify(body) }),
  reject: (id: string, note?: string, tags: DraftRejectTag[] = []) =>
    request<AiProposal>(`/ai/proposals/${id}/reject`, aiProposalSchema, { method: "POST", body: JSON.stringify({ note, tags }) }),
  suggestions: (siteId: string) => request(`/ai/memory/suggestions${qs({ siteId })}`, feedbackSuggestionsResponseSchema),
  plagiarism: (contentId: string, text?: string) =>
    request("/ai/plagiarism", plagiarismResponseSchema, { method: "POST", body: JSON.stringify({ contentId, ...(text !== undefined ? { text } : {}) }) }),

  handoff: (contentId: string) => request<HandoffStateResponse>(`/ai/handoff/${contentId}`, handoffStateResponseSchema),
  takeover: (contentId: string) =>
    request<HandoffStateResponse>(`/ai/handoff/${contentId}/takeover`, handoffStateResponseSchema, { method: "POST" }),
  heartbeat: (contentId: string) =>
    request<HandoffStateResponse>(`/ai/handoff/${contentId}/heartbeat`, handoffStateResponseSchema, { method: "POST" }),
  handback: (contentId: string) =>
    request<HandoffStateResponse>(`/ai/handoff/${contentId}/handback`, handoffStateResponseSchema, { method: "POST" }),
  pause: (contentId: string) =>
    request<HandoffStateResponse>(`/ai/handoff/${contentId}/pause`, handoffStateResponseSchema, { method: "POST" }),

  memorySummary: (siteId: string) => request(`/ai/memory/summary${qs({ siteId })}`, memorySummaryResponseSchema),
  rules: (siteId: string) =>
    request(`/ai/memory/rules${qs({ siteId })}`, listMemoryRulesResponseSchema).then((r) => r.items as MemoryRule[]),
  createRule: (siteId: string, text: string) =>
    request<MemoryRule>("/ai/memory/rules", memoryRuleSchema, { method: "POST", body: JSON.stringify({ siteId, text }) }),
  decideRule: (id: string, decision: "approved" | "rejected") =>
    request<MemoryRule>(`/ai/memory/rules/${id}/decide`, memoryRuleSchema, { method: "POST", body: JSON.stringify({ decision }) }),
  deleteRule: (id: string) =>
    request<{ ok: true }>(`/ai/memory/rules/${id}`, z.object({ ok: z.literal(true) }), { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Evidence + background runs
// ---------------------------------------------------------------------------

export const evidenceApi = {
  list: (q: Partial<EvidenceQuery> & { siteId: string }) =>
    request<EvidenceListResponse>(
      `/evidence${qs({ siteId: q.siteId, kind: q.kind, label: q.label, status: q.status, q: q.q, limit: q.limit, offset: q.offset })}`,
      evidenceListResponseSchema,
    ),
  add: (body: EvidenceInput) =>
    request<{ id: string }>("/evidence", z.object({ id: z.string() }).passthrough(), { method: "POST", body: JSON.stringify(body) }),
  retire: (id: string, reason?: string) =>
    request<{ id: string }>(`/evidence/${encodeURIComponent(id)}/retire`, z.object({ id: z.string() }).passthrough(), {
      method: "POST",
      body: JSON.stringify(reason ? { reason } : {}),
    }),
};

export const jobsApi = {
  list: (siteId: string, limit = 10): Promise<JobRun[]> =>
    request(`/jobs${qs({ siteId, limit })}`, jobsResponseSchema).then((r) => r.items),
};

// ---------------------------------------------------------------------------
// Brain: brand profile + knowledge library
// ---------------------------------------------------------------------------

export const brainApi = {
  profile: (siteId: string) => request<BrandProfileResponse>(`/brain/profile${qs({ siteId })}`, brandProfileResponseSchema),
  saveProfile: (siteId: string, body: BrandProfile) =>
    request<BrandProfileResponse>("/brain/profile", brandProfileResponseSchema, { method: "PUT", body: JSON.stringify({ siteId, ...body }) }),
  knowledge: (siteId: string) => request<KnowledgeListResponse>(`/brain/knowledge${qs({ siteId })}`, knowledgeListResponseSchema),
  addKnowledge: (body: KnowledgeDocInput) =>
    request<KnowledgeDoc>("/brain/knowledge", knowledgeDocSchema, { method: "POST", body: JSON.stringify(body) }),
  retireKnowledge: (siteId: string, id: string) =>
    request<{ id: string }>(`/brain/knowledge/${encodeURIComponent(id)}/retire${qs({ siteId })}`, z.object({ id: z.string() }).passthrough(), { method: "POST", body: "{}" }),
};

// ---------------------------------------------------------------------------
// Keyword data (DataForSEO)
// ---------------------------------------------------------------------------

export const seoApi = {
  status: (siteId: string) => request<SeoStatus>(`/seo/status${qs({ siteId })}`, seoStatusSchema),
  refreshKeywords: (body: { siteId: string; keywords: string[]; location: SeoLocationKey; languageCode: string }) =>
    request<KeywordRefreshResult>("/seo/keywords/refresh", keywordRefreshResultSchema, { method: "POST", body: JSON.stringify(body) }),
};

// ---------------------------------------------------------------------------
// Social posts (written here, approved by a second person)
// ---------------------------------------------------------------------------

const socialPostsOnly = z.object({ items: z.array(socialPostSchema) });
export const socialApi = {
  status: (siteId: string) => request(`/social/status${qs({ siteId })}`, socialStatusResponseSchema),
  addChannel: (body: { siteId: string; name: string; service: SocialChannelService }) =>
    request<SocialChannel>("/social/channels", socialChannelSchema, { method: "POST", body: JSON.stringify(body) }),
  removeChannel: (siteId: string, channelId: string) => request(`/social/channels/${encodeURIComponent(channelId)}${qs({ siteId })}`, z.object({ ok: z.boolean() }), { method: "DELETE" }),
  setChannel: (siteId: string, channelId: string, enabled: boolean) =>
    request<SocialChannel>(`/social/channels/${encodeURIComponent(channelId)}`, socialChannelSchema, { method: "PATCH", body: JSON.stringify({ siteId, enabled }) }),
  updateChannel: (siteId: string, channelId: string, patch: Omit<UpdateChannelBody, "siteId">) =>
    request<SocialChannel>(`/social/channels/${encodeURIComponent(channelId)}`, socialChannelSchema, { method: "PATCH", body: JSON.stringify({ siteId, ...patch }) }),
  reuse: (id: string) => request<SocialPost>(`/social/posts/${id}/reuse`, socialPostSchema, { method: "POST", body: "{}" }),
  importCsv: (siteId: string, csv: string) => request("/social/posts/import", importSocialCsvResponseSchema, { method: "POST", body: JSON.stringify({ siteId, csv }) }),
  flow: (siteId: string) => request(`/social/flow${qs({ siteId })}`, socialFlowSchema),
  posts: (siteId: string, status?: SocialStatus) => request(`/social/posts${qs({ siteId, status, limit: 100 })}`, socialPostsResponseSchema),
  /** A searchable, filterable, paged list of posts (the server counts the true total). */
  postPage: (q: { siteId: string; status?: SocialStatus; stage?: string; topic?: string; q?: string; channelId?: string; limit: number; offset: number }) => request(`/social/posts${qs(q)}`, socialPostsResponseSchema),
  stages: (siteId: string) => request(`/social/stages${qs({ siteId })}`, socialStageCountsSchema),
  create: (body: CreateSocialPostsBody) => request("/social/posts", socialPostsOnly, { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: { text?: string; scheduledFor?: string | null; imageUrls?: string[]; videoUrl?: string | null; youtubeTitle?: string | null; topic?: string | null }) =>
    request<SocialPost>(`/social/posts/${id}`, socialPostSchema, { method: "PATCH", body: JSON.stringify(body) }),
  /** `seenUpdatedAt`: the version the person was looking at; approving is refused if the post changed since. */
  act: (id: string, action: "submit" | "approve" | "cancel", seenUpdatedAt?: string) =>
    request<SocialPost>(`/social/posts/${id}/${action}`, socialPostSchema, { method: "POST", body: JSON.stringify(seenUpdatedAt ? { updatedAt: seenUpdatedAt } : {}) }),
  requestChanges: (id: string, note: string) =>
    request<SocialPost>(`/social/posts/${id}/request-changes`, socialPostSchema, { method: "POST", body: JSON.stringify({ note }) }),
  review: (id: string) => request(`/social/posts/${id}/review`, socialReviewSchema),
  insights: (siteId: string) => request(`/social/insights${qs({ siteId })}`, socialInsightsSchema),
  ideaSuggestions: (siteId: string) => request(`/social/idea-suggestions${qs({ siteId })}`, socialIdeasSchema),
  agents: (siteId: string) => request(`/social/agents${qs({ siteId })}`, socialAgentsSchema),
  switchRule: (body: { siteId: string; key: string; on: boolean }) => request("/social/agents/rules/switch", socialAgentsSchema, { method: "POST", body: JSON.stringify(body) }),
  runCheck: (siteId: string) => request(`/social/agents/run${qs({ siteId })}`, z.object({ ok: z.boolean(), view: socialAgentsSchema }), { method: "POST" }),
  comment: (id: string, body: { kind: "comment"; body: string } | { kind: "suggestion"; from: string; to: string }) =>
    request(`/social/posts/${id}/comments`, z.object({ id: z.string() }), { method: "POST", body: JSON.stringify(body) }),
  answerSuggestion: (commentId: string, answer: "accept" | "decline") =>
    request(`/social/comments/${commentId}/answer`, z.object({ answer: z.enum(["accept", "decline"]) }), { method: "POST", body: JSON.stringify({ answer }) }),
  setTime: (id: string, scheduledFor: string) =>
    request(`/social/posts/${id}/time`, z.object({ scheduledFor: z.string().nullable(), updatedAt: z.string() }), { method: "POST", body: JSON.stringify({ scheduledFor }) }),
};

/** Ads research: the watchlist, competitor ads a person observed, and our own ad results. */
export const adsApi = {
  resultsByTag: (q: { siteId: string; product?: AdProductId; channel?: AdChannel; weeks?: number }) => request(`/ads/results/by-tag${qs(q)}`, byTagResponseSchema),
  watchlist: (siteId: string, includeRetired = false, product?: AdProductId) => request(`/ads/watchlist${qs({ siteId, product, includeRetired: includeRetired ? "true" : "false" })}`, watchlistResponseSchema),
  addWatch: (body: WatchlistInput) => request("/ads/watchlist", watchlistItemSchema, { method: "POST", body: JSON.stringify(body) }),
  patchWatch: (id: string, body: { siteId: string; pinned?: boolean; reason?: string; category?: "direct_competitor" | "trendsetter"; domain?: string | null }) =>
    request(`/ads/watchlist/${id}`, watchlistItemSchema, { method: "PATCH", body: JSON.stringify(body) }),
  retireWatch: (id: string, siteId: string, reason: string) => request(`/ads/watchlist/${id}/retire`, watchlistItemSchema, { method: "POST", body: JSON.stringify({ siteId, reason }) }),
  restoreWatch: (id: string, siteId: string) => request(`/ads/watchlist/${id}/restore`, watchlistItemSchema, { method: "POST", body: JSON.stringify({ siteId }) }),

  competitorAds: (q: { siteId: string; product?: AdProductId; watchlistId?: string; category?: string; includeRetired?: boolean; sort?: "importance" | "recent"; page?: number; pageSize?: number }) =>
    request(`/ads/competitor-ads${qs({ ...q, includeRetired: q.includeRetired ? "true" : undefined })}`, competitorAdsResponseSchema),
  addCompetitorAd: (body: CompetitorAdInput) => request("/ads/competitor-ads", competitorAdSchema, { method: "POST", body: JSON.stringify(body) }),
  updateCompetitorAd: (id: string, body: CompetitorAdInput) => request(`/ads/competitor-ads/${id}`, competitorAdSchema, { method: "PUT", body: JSON.stringify(body) }),
  retireCompetitorAd: (id: string, siteId: string) => request(`/ads/competitor-ads/${id}/retire`, competitorAdSchema, { method: "POST", body: JSON.stringify({ siteId }) }),
  apifyStatus: () => request("/ads/apify/status", apifyStatusSchema),
  advertisers: (body: { siteId: string; product: AdProductId; phrase: string; market: "us" | "ca"; limit: number }) =>
    request("/ads/semrush/advertisers", advertisersResultSchema, { method: "POST", body: JSON.stringify(body) }),
  advertiserDetail: (body: { siteId: string; product: AdProductId; domain: string; kind: "ads" | "keywords" | "history"; market: "us" | "ca"; limit: number }) =>
    request("/ads/semrush/domain", domainResultSchema, { method: "POST", body: JSON.stringify(body) }),
  flow: (q: { siteId: string; product: AdProductId; channel: AdChannel }) => request(`/ads/flow${qs(q)}`, flowSchema),
  keywordLookup: (body: { siteId: string; product: AdProductId; phrase: string; limit: number }) =>
    request("/ads/semrush/keywords", keywordLookupResultSchema, { method: "POST", body: JSON.stringify(body) }),
  fetchCompetitorAds: (siteId: string, watchlistId: string, maxAds: number) =>
    request("/ads/competitor-ads/fetch", apifyFetchResultSchema, { method: "POST", body: JSON.stringify({ siteId, watchlistId, maxAds }) }),
  importCompetitorAds: (siteId: string, product: AdProductId, csv: string, fileName: string) =>
    request("/ads/competitor-ads/import", adsImportResponseSchema, { method: "POST", body: JSON.stringify({ siteId, product, csv, fileName }) }),

  results: (q: { siteId: string; product?: AdProductId; page?: number; pageSize?: number }) => request(`/ads/results${qs(q)}`, adResultsResponseSchema),
  addResult: (body: AdResultInput) => request("/ads/results", adResultSchema, { method: "POST", body: JSON.stringify(body) }),
  importResults: (siteId: string, product: AdProductId, csv: string, fileName: string, channel: AdChannel = "linkedin") =>
    request("/ads/results/import", adsImportResponseSchema, { method: "POST", body: JSON.stringify({ siteId, product, csv, fileName, channel }) }),
  summary: (siteId: string, product?: AdProductId) => request(`/ads/summary${qs({ siteId, product })}`, adsSummarySchema),

  batches: (siteId: string) => request(`/ads/batches${qs({ siteId })}`, adsBatchesResponseSchema),
  undoBatch: (id: string, siteId: string) => request(`/ads/batches/${id}/retire`, z.object({ ok: z.boolean() }), { method: "POST", body: JSON.stringify({ siteId }) }),
};

/** Ad drafts waiting for a person: list, edit, review. */
const ruleDecisionSchema = z.object({ id: z.string(), status: z.string(), verifiedAt: z.string() });

export const adDraftsApi = {
  list: (q: { siteId: string; status?: AdStatus; product?: AdProductId; channel?: AdChannel; limit?: number; offset?: number; reference?: boolean }) => request(`/ad-drafts${qs(q)}`, adDraftsResponseSchema),
  edit: (id: string, siteId: string, payload: AdPayload) => request(`/ad-drafts/${id}`, adDraftSchema, { method: "PUT", body: JSON.stringify({ siteId, payload }) }),
  generate: (body: { siteId: string; product: AdProductId; channel: AdChannel; count: number; target?: { adGroup: string; keyword: string }; origin?: AdOrigin }) =>
    request("/ad-drafts/generate", adGenerateResultSchema, { method: "POST", body: JSON.stringify(body) }),
  regenerate: (id: string, siteId: string, note: string) => request(`/ad-drafts/${id}/regenerate`, adGenerateResultSchema, { method: "POST", body: JSON.stringify({ siteId, note }) }),
  lineage: (id: string, siteId: string) => request(`/ad-drafts/${id}/lineage${qs({ siteId })}`, adLineageSchema),
  /** Asks the second-opinion reviewers again and replaces the reviewer notes. */
  reviewAgain: (id: string, siteId: string) => request(`/ad-drafts/${id}/review-again`, adDraftSchema, { method: "POST", body: JSON.stringify({ siteId }) }),
  /** A manager keeps an ad as a copy reference for the AI, or stops. */
  setReference: (id: string, siteId: string, on: boolean) => request(`/ad-drafts/${id}/reference`, adDraftSchema, { method: "POST", body: JSON.stringify({ siteId, on }) }),
  learnedRules: (q: { siteId: string; product: AdProductId; channel: AdChannel; status?: LearnedStatus }) => request(`/ad-drafts/learned-rules${qs(q)}`, adLearnedRulesResponseSchema),
  verifyRule: (id: string, siteId: string) => request(`/ad-drafts/learned-rules/${id}/verify`, ruleDecisionSchema, { method: "POST", body: JSON.stringify({ siteId }) }),
  rejectRule: (id: string, siteId: string) => request(`/ad-drafts/learned-rules/${id}/reject`, ruleDecisionSchema, { method: "POST", body: JSON.stringify({ siteId }) }),
  verifyAllRules: (body: { siteId: string; product: AdProductId; channel: AdChannel }) => request("/ad-drafts/learned-rules/verify-all", z.object({ count: z.number() }), { method: "POST", body: JSON.stringify(body) }),
  /** Brand kit → Images → Test style: one throwaway picture from the settings on screen. */
  testImageStyle: (body: { siteId: string; product: AdProductId; image: Pick<AdImageKit, "style" | "guardrails">; hook?: string }) => request("/ad-drafts/image-style/test", adSceneResultSchema, { method: "POST", body: JSON.stringify(body) }),
  /** Makes the AI scene behind a LinkedIn ad again; `note` is added to the picture prompt. */
  scene: (id: string, siteId: string, note: string) => request(`/ad-drafts/${id}/scene`, adSceneResultSchema, { method: "POST", body: JSON.stringify({ siteId, note }) }),
  review: (id: string, body: { siteId: string; verdict: AdVerdict; tags: AdFeedbackTag[]; note?: string }) =>
    request(`/ad-drafts/${id}/review`, adDraftSchema, { method: "POST", body: JSON.stringify(body) }),
  /** Builds the Google Ads Editor file from approved Google ads (managers). */
  /** A manager takes an ad out of the list (never deleted). */
  retire: (id: string, siteId: string) => request(`/ad-drafts/${id}/retire`, adDraftSchema, { method: "POST", body: JSON.stringify({ siteId }) }),
  exportGoogleEditor: (body: { siteId: string; product: AdProductId; again: boolean }) =>
    request("/ad-drafts/export/google-editor", adEditorExportSchema, { method: "POST", body: JSON.stringify(body) }),
  editorFiles: (siteId: string) => request(`/ad-drafts/export/google-editor${qs({ siteId })}`, adEditorExportListSchema),
  editorFile: (id: string, siteId: string) => request(`/ad-drafts/export/google-editor/${id}${qs({ siteId })}`, adEditorExportSchema),
};

export const adsHistoryApi = {
  concepts: (q: { siteId: string; product: AdProductId }) => request(`/ads/concepts${qs(q)}`, adConceptsResponseSchema),
  reviewConcept: (id: string, body: { siteId: string; verdict: AdVerdict; tags: AdFeedbackTag[]; note?: string }) =>
    request(`/ads/concepts/${id}/review`, z.object({ ok: z.literal(true) }), { method: "POST", body: JSON.stringify(body) }),
  researchRuns: (q: { siteId: string }) => request(`/ads/research-runs${qs(q)}`, adResearchRunsResponseSchema),
  feedbackRules: (q: { siteId: string; product: AdProductId; channel: AdChannel }) => request(`/ad-drafts/feedback-rules${qs(q)}`, adFeedbackRulesResponseSchema),
};

export const adInsightsApi = {
  get: (siteId: string, product: AdProductId) => request(`/ads/insights${qs({ siteId, product })}`, insightsSchema),
  answer: (body: { siteId: string; product: AdProductId; key: string; answer: AlertAnswer }) => request("/ads/insights/answer", alertAnswerResultSchema, { method: "POST", body: JSON.stringify(body) }),
};

export const adsAgentsApi = {
  get: (siteId: string, product: AdProductId) => request(`/ads/agents${qs({ siteId, product })}`, agentsViewSchema),
};

export const adBrandApi = {
  get: (siteId: string, product: AdProductId) => request(`/ads/brand-kit${qs({ siteId, product })}`, brandKitViewSchema),
  save: (siteId: string, product: AdProductId, kit: BrandKit, image?: Pick<AdImageKit, "style" | "guardrails">) => request("/ads/brand-kit", brandKitViewSchema, { method: "PUT", body: JSON.stringify({ siteId, product, kit, image }) }),
};

export const connectionsApi = {
  list: (siteId: string) => request(`/connections${qs({ siteId })}`, connectionsResponseSchema),
};

export const adGoogleApi = {
  items: (siteId: string, product: AdProductId) => request(`/ads/google-items${qs({ siteId, product })}`, googleItemsResponseSchema),
  get: (siteId: string) => request(`/ads/google/connection${qs({ siteId })}`, googleConnectionSchema),
  connect: (siteId: string) => request("/ads/google/connection", googleConnectionCreatedSchema, { method: "POST", body: JSON.stringify({ siteId }) }),
  disconnect: (siteId: string) => request(`/ads/google/connection${qs({ siteId })}`, z.object({ ended: z.boolean() }), { method: "DELETE" }),
};

export const adScheduleApi = {
  get: (siteId: string) => request(`/ad-schedule${qs({ siteId })}`, adScheduleStatusSchema),
  save: (siteId: string, schedule: AdSchedule) => request("/ad-schedule", adScheduleStatusSchema, { method: "PUT", body: JSON.stringify({ siteId, schedule }) }),
};

/** What ads cost per week and product. */
export const adCostsApi = {
  get: (siteId: string, weeks = 8) => request(`/ads/costs${qs({ siteId, weeks })}`, adCostReportSchema),
};

/** Customer names, logos and quotes ads may use only after the customer said yes in writing. */
export const adNameApprovalsApi = {
  list: (siteId: string) => request(`/ad-name-approvals${qs({ siteId })}`, adNameApprovalListSchema),
  add: (body: { siteId: string; name: string; kind: "name" | "logo" | "quote" | "claim"; product: AdProductId | null; covers: string; figures: string[] }) =>
    request("/ad-name-approvals", adNameApprovalSchema, { method: "POST", body: JSON.stringify(body) }),
  approve: (id: string, writtenOk: string) => request(`/ad-name-approvals/${id}/approve`, adNameApprovalSchema, { method: "POST", body: JSON.stringify({ writtenOk }) }),
  retire: (id: string) => request(`/ad-name-approvals/${id}`, z.object({ ok: z.literal(true) }), { method: "DELETE" }),
};

/** Sending approved social posts through Sociamonials. */
export const sociamonialsApi = {
  status: (siteId: string) => request(`/sociamonials/status${qs({ siteId })}`, smStatusResponseSchema),
  save: (body: { siteId: string; workspaceId: number | null; workspaceName: string | null; sendEnabled: boolean; autoSend: boolean; postPreset?: string | null }) =>
    request("/sociamonials/connection", smStatusResponseSchema, { method: "PUT", body: JSON.stringify(body) }),
  test: () => request("/sociamonials/test", smTestResultSchema, { method: "POST", body: "{}" }),
  sync: (siteId: string) => request("/sociamonials/sync-profiles", smSyncResultSchema, { method: "POST", body: JSON.stringify({ siteId }) }),
  connectAlerts: (siteId: string) => request("/sociamonials/alerts/connect", smStatusResponseSchema, { method: "POST", body: JSON.stringify({ siteId }) }),
  send: (postId: string) => request(`/sociamonials/send/${postId}`, smSendResultSchema, { method: "POST", body: "{}" }),
  analytics: (siteId: string, range: SmRange) => request(`/sociamonials/analytics${qs({ siteId, range })}`, smAnalyticsResponseSchema),
};

// ---------------------------------------------------------------------------
// v92: Social pipeline, prompts and Operations
// ---------------------------------------------------------------------------

const problemsOnly = z.object({ problems: z.array(z.object({ rule: z.string(), message: z.string(), severity: z.enum(["fix", "advice"]), where: z.string(), flagged: z.boolean().optional() })) });

export const socialPipelineApi = {
  ideas: (q: { siteId: string; product: AdProductId; status?: "open" | "used" | "dismissed" }) => request(`/social/ideas${qs(q)}`, savedSocialIdeaListSchema),
  generateIdeas: (body: GenerateSocialIdeasBody) => request("/social/ideas/generate", generateSocialIdeasResultSchema, { method: "POST", body: JSON.stringify(body) }),
  dismissIdea: (id: string, siteId: string) => request(`/social/ideas/${id}/dismiss`, z.object({ id: z.string(), status: z.literal("dismissed") }), { method: "POST", body: JSON.stringify({ siteId }) }),
  generatePosts: (body: GenerateSocialPostsBody) => request("/social/posts/generate", generateSocialPostsResultSchema, { method: "POST", body: JSON.stringify(body) }),
  reviewAgain: (id: string) => request(`/social/posts/${id}/review-again`, problemsOnly, { method: "POST", body: "{}" }),
  makeScene: (id: string) => request(`/social/posts/${id}/scene`, z.object({ sceneImageId: z.string(), costUsd: z.number() }), { method: "POST", body: "{}" }),
  setVerdictTags: (id: string, tags: AdFeedbackTag[]) => request(`/social/posts/${id}/verdict-tags`, z.object({ id: z.string() }).passthrough(), { method: "POST", body: JSON.stringify({ tags }) }),
  setReference: (id: string, on: boolean) => request(`/social/posts/${id}/reference`, z.object({ id: z.string(), isReference: z.boolean() }), { method: "POST", body: JSON.stringify({ on }) }),
  learnedRules: (q: { siteId: string; product: AdProductId }) => request(`/social/learned-rules${qs(q)}`, socialLearnedRulesResponseSchema),
  verifyRule: (id: string, siteId: string) => request(`/social/learned-rules/${id}/verify`, z.object({ id: z.string() }).passthrough(), { method: "POST", body: JSON.stringify({ siteId }) }),
  rejectRule: (id: string, siteId: string) => request(`/social/learned-rules/${id}/reject`, z.object({ id: z.string() }).passthrough(), { method: "POST", body: JSON.stringify({ siteId }) }),
  verifyAll: (siteId: string, product: AdProductId) => request("/social/learned-rules/verify-all", z.object({ count: z.number().int() }), { method: "POST", body: JSON.stringify({ siteId, product }) }),
  thisWeek: (q: { siteId: string; product: AdProductId }) => request(`/social/this-week${qs(q)}`, socialThisWeekSchema),
  testChannel: (siteId: string, channelId: string) => request(`/social/channels/${encodeURIComponent(channelId)}/test`, z.object({ ok: z.boolean(), message: z.string() }), { method: "POST", body: JSON.stringify({ siteId }) }),
};

export const promptsApi = {
  list: (siteId: string) => request(`/prompts${qs({ siteId })}`, promptListSchema),
  save: (key: EditablePromptKey, body: { siteId: string; body: string; note?: string }) => request(`/prompts/${key}`, z.object({ key: z.string(), version: z.number().int() }), { method: "PUT", body: JSON.stringify(body) }),
  test: (body: { siteId: string; key: EditablePromptKey; body: string; itemId: string }) => request("/prompts/test", testPromptResultSchema, { method: "POST", body: JSON.stringify(body) }),
};

export const opsApi = {
  runs: (siteId: string, limit = 100) => request(`/ops/runs${qs({ siteId, limit })}`, opsRunsResponseSchema),
};
